import { useEffect, useRef } from 'react';
import styles from './ContextMenu.module.css';

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  // Destructive items are tinted rather than separated, matching the delete entry
  // on the treatment plan template card.
  isDestructive?: boolean;
  // Shown but inert — the row menu keeps "move up" on the first row so the list
  // does not change length between rows.
  isDisabled?: boolean;
}

export interface ContextMenuProps {
  anchorRect: DOMRect;
  items: ContextMenuItem[];
  onClose: () => void;
}

export const ContextMenu = ({ anchorRect, items, onClose }: ContextMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;

    const menuRect = menu.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = anchorRect.bottom + 4;
    let left = anchorRect.left;

    if (top + menuRect.height > viewportHeight) {
      top = anchorRect.top - menuRect.height - 4;
    }
    if (left + menuRect.width > viewportWidth) {
      left = viewportWidth - menuRect.width - 8;
    }

    menu.style.top = `${String(top)}px`;
    menu.style.left = `${String(left)}px`;
  }, [anchorRect]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    menuRef.current?.querySelector('button')?.focus();

    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const menu = menuRef.current;
    if (!menu) return;

    e.preventDefault();
    const buttons = Array.from(menu.querySelectorAll('button'));
    if (buttons.length === 0) return;
    const currentIndex = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const step = e.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = (currentIndex + step + buttons.length) % buttons.length;
    buttons[nextIndex].focus();
  };

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div
        ref={menuRef}
        className={styles.menu}
        style={{ position: 'fixed' }}
        role="menu"
        tabIndex={-1}
        onKeyDown={handleMenuKeyDown}
      >
        {items.map((item) => (
          <button
            key={item.label}
            type="button"
            role="menuitem"
            // aria-disabled rather than the disabled attribute: a disabled
            // button cannot take focus, which would drop it out of the arrow-key
            // walk in handleMenuKeyDown.
            aria-disabled={item.isDisabled === true}
            className={`${styles.menuItem} ${item.isDestructive === true ? styles.menuItemDestructive : ''} ${item.isDisabled === true ? styles.menuItemDisabled : ''}`}
            onClick={() => {
              if (item.isDisabled === true) return;
              item.onClick();
              onClose();
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
};
